import { ViewModelForm } from 'gw-portals-viewmodel-react';
import React, {
    useEffect, useContext, useMemo, useCallback, useState, useRef
} from 'react';
import _ from 'lodash';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { SgSessionStorageService, useSgHandleSubQuestions } from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import { SgDate, SgDateInputTypes } from 'sg-date';
import metadata from './SgOccupationDetails.metadata.json5';
import messages from './SgOccupationDetails.messages';
import SgLookup from './sg-lookup';

function SgOccupationDetails(props) {
    const {
        id,
        modelVM,
        showErrors,
        updateData,
        path,
        messageParams,
        retirementDateQuestionAvailable,
        onValidate: onValidateParent,
        adobeIdSuffix
    } = props;

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const [invalidSubQuestions, updateInvalidSubQuestions] = useState(
        SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS)
    );
    const [employmentStatusOtherSelected, setEmploymentStatusOtherSelected] = useState(false);
    const personModel = _.get(modelVM, path);
    const occupationModel = _.get(modelVM, `${path}.occupation`);
    const businessTypeModel = _.get(modelVM, `${path}.businessType`);
    const resetOccupationInput = useRef();
    const resetBusinessInput = useRef();
    const employmentStatusOptions = [CommonConstants.EMPLOYMENT_STATUS_EMPLOYED, CommonConstants.EMPLOYMENT_STATUS_RETIRED,
        CommonConstants.EMPLOYMENT_STATUS_SELF_EMPLOYED, CommonConstants.EMPLOYMENT_STATUS_OTHER];
    const optionsWhenOtherStatusIsNotSelected = [CommonConstants.EMPLOYMENT_STATUS_EMPLOYED, CommonConstants.EMPLOYMENT_STATUS_RETIRED,
        CommonConstants.EMPLOYMENT_STATUS_SELF_EMPLOYED];
    const showPartTimeOccupationLabel = personModel.partTimeOccupationType.value !== undefined
        && personModel.partTimeOccupationType.value !== CommonConstants.EMPLOYMENT_STATUS_OTHER;

    const updateEmploymentStatus = useCallback((value) => {
        const newModelVM = _.clone(modelVM);
        if (value.code === CommonConstants.EMPLOYMENT_STATUS_OTHER) {
            setEmploymentStatusOtherSelected(true);
            _.set(newModelVM, `${path}.employmentStatus`, undefined);
        } else {
            _.set(newModelVM, `${path}.employmentStatus`, value);
            _.set(newModelVM, `${path}.partTimeOccupation.value`, undefined);
            _.set(newModelVM, `${path}.occupation`, undefined);
            _.set(newModelVM, `${path}.businessType`, undefined);
            setEmploymentStatusOtherSelected(false);
        }
        updateData(newModelVM);
        if (!_.isNil(resetOccupationInput.current)) {
            resetOccupationInput.current();
        }
        if (!_.isNil(resetBusinessInput.current)) {
            resetBusinessInput.current();
        }
    }, [modelVM, updateData]);

    const resetPartTimeOccupation = useCallback((value) => {
        const data = personModel.value.employmentStatus;
        if (value !== data) {
            _.set(modelVM, `${path}.employmentStatus`, value);
            _.set(modelVM, `${path}.partTimeOccupation.value`, undefined);
            updateData(modelVM);
        }
    }, [modelVM, updateData]);

    useEffect(() => {
        if (personModel.employmentStatus.value !== undefined
            && !optionsWhenOtherStatusIsNotSelected.includes(personModel.employmentStatus.value.code)) {
            setEmploymentStatusOtherSelected(true);
        }
    }, [personModel.employmentStatus.value]);

    const employmentStatusValues = useMemo(() => {
        const employmentStatusAvailableValues = [];
        const filteredEmploymentStatusOptions = personModel.employmentStatus.aspects.availableValues.filter((option) => {
            return employmentStatusOptions.includes(option.code);
        });
        _.each(filteredEmploymentStatusOptions, (statusType) => {
            employmentStatusAvailableValues.push({
                code: statusType.code,
                name: translator({
                    id: statusType.name,
                    defaultMessage: statusType.name
                })
            });
        });
        return employmentStatusAvailableValues;
    }, [personModel.employmentStatus, translator]);

    const otherEmploymentStatusValues = useMemo(() => {
        const otherEmploymentStatusAvailableValues = [];
        const filteredOtherEmploymentStatusOptions = personModel.employmentStatus.aspects.availableValues.filter((option) => {
            return !employmentStatusOptions.includes(option.code);
        });
        _.each(filteredOtherEmploymentStatusOptions, (statusType) => {
            otherEmploymentStatusAvailableValues.push({
                code: statusType.code,
                name: translator({
                    id: statusType.name,
                    defaultMessage: statusType.name
                })
            });
        });
        return otherEmploymentStatusAvailableValues;
    }, [personModel.employmentStatus, translator]);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        if (personModel.partTimeOccupation.value === false || personModel.partTimeOccupation.value === undefined) {
            personModel.partTimeOccupationType.value = undefined;
        }
    }, [personModel.partTimeOccupation.value]);

    // clear values of hidden fields
    useSgHandleSubQuestions({
        updateModel: updateData,
        updateInvalidSubQuestions,
        modelVM,
        paths: [
            `${path}.partTimeOccupation`,
            `${path}.studentType`,
            `${path}.occupation`,
            `${path}.businessType`,
            `${path}.approximateRetirementDate`,
            `${path}.employmentStatus`,
            {
                path: `${path}.partTimeOccupationType`,
                required: personModel.partTimeOccupation.value === true,
            }
        ]
    });

    const showApproxRetirementDate = useMemo(() => {
        if (!retirementDateQuestionAvailable) {
            return false;
        }
        if (personModel.employmentStatus && personModel.employmentStatus.value) {
            return personModel.employmentStatus.value.code === CommonConstants.EMPLOYMENT_STATUS_EMPLOYED
                || personModel.employmentStatus.value.code === CommonConstants.EMPLOYMENT_STATUS_SELF_EMPLOYED;
        }
        return false;
    }, [personModel.employmentStatus.value, retirementDateQuestionAvailable]);

    const overrides = {
        employmentStatus: {
            path: `${path}.employmentStatus`,
            label: translator(messages.EmploymentStatus, messageParams),
            secondaryLabel: translator(messages.EmploymentStatusSubLabel, messageParams),
            availableValues: employmentStatusValues,
            onValueChange: updateEmploymentStatus,
            value: employmentStatusOtherSelected ? { code: CommonConstants.EMPLOYMENT_STATUS_OTHER }
                : _.get(modelVM, `${path}.employmentStatus.value`),
            validationMessages: !employmentStatusOtherSelected && (_.get(modelVM, `${path}.employmentStatus.value`) === undefined)
                ? _.get(modelVM, `${path}.employmentStatus`).aspects.validationMessages : [],
            adobeID: `employmentStatusSelect${adobeIdSuffix}`,
        },
        employmentStatusSelect: {
            visible: employmentStatusOtherSelected,
            path: `${path}.employmentStatus`,
            availableValues: otherEmploymentStatusValues,
            onValueChange: resetPartTimeOccupation,
            value: _.get(modelVM, `${path}.employmentStatus.value`),
            adobeID: `employmentStatusSelect${adobeIdSuffix}`,
            showErrors: invalidSubQuestions.includes(`${path}.employmentStatus`),
        },
        partTimeOccupation: {
            path: `${path}.partTimeOccupation`,
            visible: personModel.partTimeOccupation.aspects.required,
            label: translator(messages.PartTimeOccupationLabel, messageParams),
            showErrors: invalidSubQuestions.includes(`${path}.partTimeOccupation`),
            adobeID: `partTimeOccupation${adobeIdSuffix}`,
        },
        partTimeOccupationTypeSelect: {
            path: `${path}.partTimeOccupationType`,
            visible: personModel.partTimeOccupation.value === true,
            label: translator(messages.partTimeOccupationTypeSelect, messageParams),
            showErrors: invalidSubQuestions.includes(`${path}.partTimeOccupationType`)
        },
        occupationLookupTypeahead: {
            visible: occupationModel.aspects.required,
            modelVM,
            updateData,
            lookupPath: `${path}.occupation`,
            lookupModel: occupationModel,
            lookupValue: _.get(modelVM, `${path}.occupation.value`, {}),
            lookupLabel: showPartTimeOccupationLabel ? translator(messages.PartTimeOccupationLookupLabel, messageParams)
                : translator(messages.OccupationLookupLabel, messageParams),
            lookupSecondaryLabel: translator(messages.OccupationLookupSubLabel, messageParams),
            lookupTopDropdownText: translator(messages.OccupationLookupTopDropdownText),
            lookupBottomDropdownText: translator(messages.OccupationLookupBottomDropdownText),
            lookupResultNotFoundMessage: translator(messages.OccupationLookupResultNotFoundMessage),
            lookupErrorMessage: translator(messages.OccupationLookupErrorMessage),
            lookupPlaceholderMessage: translator(messages.OccupationLookupPlaceHolder),
            showErrors: showErrors && invalidSubQuestions.includes(`${path}.occupation`),
            onValidate: onValidate,
            resetRawInput: resetOccupationInput,
            adobeID: `occupationTypeSelect${adobeIdSuffix}`,
        },
        businessLookupTypeahead: {
            visible: businessTypeModel.aspects.required,
            modelVM,
            updateData,
            lookupPath: `${path}.businessType`,
            lookupModel: businessTypeModel,
            lookupValue: _.get(modelVM, `${path}.businessType.value`, {}),
            lookupLabel: translator(messages.BusinessTypeLabel),
            lookupSecondaryLabel: translator(messages.BusinessTypeSubLabel),
            lookupTopDropdownText: translator(messages.BusinessTypeTopDropdownText),
            lookupBottomDropdownText: translator(messages.BusinessTypeBottomDropdownText),
            lookupResultNotFoundMessage: translator(messages.BusinessTypeResultNotFoundMessage),
            lookupErrorMessage: translator(messages.BusinessTypeErrorMessage),
            lookupPlaceholderMessage: translator(messages.BusinessTypePlaceHolder),
            showErrors: showErrors && invalidSubQuestions.includes(`${path}.businessType`),
            onValidate: onValidate,
            resetRawInput: resetBusinessInput
        },
        approximateRetirementDate: {
            path: `${path}.approximateRetirementDate`,
            visible: showApproxRetirementDate,
            onValidate: onValidate,
            updateData,
            modelVM,
            calculateDayToSet: () => 1,
            datePickerTypeDesktop: SgDateInputTypes.MONTH_YEAR_INPUTS,
            datePickerTypeMobile: SgDateInputTypes.MONTH_YEAR_INPUTS,
            showErrors: showErrors && invalidSubQuestions.includes(`${path}.approximateRetirementDate`),
            label: translator(messages.ApproxRetirement, messageParams),
            secondaryLabel: translator(messages.ApproxRetirementSubLabel, messageParams),
        },
        studentType: {
            path: `${path}.studentType`,
            label: translator(messages.studentTypeLabel, messageParams),
            visible: personModel.studentType.aspects.required,
            showErrors: showErrors && invalidSubQuestions.includes(`${path}.studentType`),
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate
        },
        resolveComponentMap: {
            SgLookup,
            SgDate
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrides}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

SgOccupationDetails.defaultProps = {
    showApproxRetirementDate: false,
    adobeIdSuffix: 'PolicyHolder'
};

export default SgOccupationDetails;
